import { useState, useEffect, useMemo } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  Search, Layers, Users, FolderOpen, Play, LayoutGrid, Sparkles,
  List, ArrowUp, ArrowDown,
} from 'lucide-react'
import type { ProjectData } from '@/types'
import { useProjectStore } from '@/store/projectStore'
import { useDockUiStore } from '@/store/dockUiStore'
import { useAuth } from '@/auth/AuthContext'
import { isRunOnlyAccess } from '@/auth/useProjectPermissions'
import Modal from '@/components/common/Modal'

// ---------------------------------------------------------------------------
// Projects picker — opened from the left rail / Builder hub "Open project".
// Two tabs:
//   • Your projects  — projects the user owns (drafts flagged with a badge)
//   • Shared with you — projects owned by others where the user is a team member
// The list comes from GET /workbench/projects, which already merges owned +
// shared and tags each with an `access` block, and now carries filesystem
// created/last-modified timestamps used for the dates line + sorting.
//
// View mode (list/card), sort (name/created/modified + direction) and the
// modal size/maximized state are all remembered in localStorage.
// ---------------------------------------------------------------------------

type Tab = 'owned' | 'shared'
type ViewMode = 'list' | 'card'
type SortKey = 'name' | 'created' | 'updated'
type SortDir = 'asc' | 'desc'

const VIEW_KEY = 'twasta_projects_view'
const SORT_KEY = 'twasta_projects_sort'
const SORTDIR_KEY = 'twasta_projects_sortdir'
const SIZE_KEY = 'twasta_projects_modal_size'

const SORT_OPTIONS: { key: SortKey; label: string }[] = [
  { key: 'updated', label: 'Last modified' },
  { key: 'created', label: 'Created' },
  { key: 'name', label: 'Name' },
]

const readPref = <T extends string>(key: string, allowed: readonly T[], fallback: T): T => {
  try {
    const v = localStorage.getItem(key)
    return v && (allowed as readonly string[]).includes(v) ? (v as T) : fallback
  } catch {
    return fallback
  }
}
const writePref = (key: string, value: string) => {
  try { localStorage.setItem(key, value) } catch { /* private mode — just don't persist */ }
}

const prettyRole = (role: string) =>
  role
    .toLowerCase()
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ')

const isOwned = (p: ProjectData) => (p.access ? p.access.is_owner : true)
const isDraft = (p: ProjectData) =>
  p.application.group === 'draft' || !p.application.generated

const ownerInitials = (name: string) =>
  (name || '?')
    .split(/[\s@]+/)
    .filter(Boolean)
    .map((w) => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase()

const tsOf = (iso?: string) => {
  const t = iso ? Date.parse(iso) : NaN
  return Number.isNaN(t) ? 0 : t
}
const fmtDate = (iso?: string) => {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
}

// Small gradient avatar for the teammate who shared a project, matching the
// app's user-avatar style (TopBar). Identifies the owner at a glance.
function OwnerAvatar({ name }: { name: string }) {
  return (
    <span
      style={{
        width: 18,
        height: 18,
        borderRadius: 'var(--radius-full)',
        background: 'linear-gradient(135deg, #0061D0, #009974)',
        color: '#fff',
        fontSize: '0.8125rem',
        fontWeight: 700,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      {ownerInitials(name)}
    </span>
  )
}

// The application avatar: an uploaded image icon wins; otherwise the app's
// material-font icon glyph on its theme colour (initials are no longer used).
function AppAvatar({ p, size = 36 }: { p: ProjectData; size?: number }) {
  const isInvalid = p.application.title === 'Invalid project information'
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: 'var(--radius-md)',
        background: isInvalid ? 'rgba(239,68,68,0.15)' : (p.application.theme_color || 'var(--color-accent)'),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: isInvalid ? 'var(--color-error)' : '#fff',
        flexShrink: 0,
        overflow: 'hidden',
      }}
    >
      {isInvalid ? (
        <span className="material-icons" style={{ fontSize: Math.round(size * 0.56) }}>error_outline</span>
      ) : p.application.icon_image_data ? (
        <img
          src={p.application.icon_image_data}
          alt=""
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        />
      ) : (
        <span className="material-icons" style={{ fontSize: Math.round(size * 0.56) }}>
          {p.application.icon || 'apps'}
        </span>
      )}
    </div>
  )
}

function DraftBadge() {
  return (
    <span
      style={{
        flexShrink: 0,
        fontSize: '0.8125rem',
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        padding: '1px 6px',
        borderRadius: 'var(--radius-full)',
        background: 'var(--color-bg)',
        color: 'var(--color-text-secondary)',
        border: '1px solid var(--color-border)',
      }}
    >
      Draft
    </span>
  )
}

export default function ProjectsModal() {
  const navigate = useNavigate()
  const location = useLocation()
  const { user } = useAuth()
  const { projects, currentProject, fetchProjects, selectProject, setActiveTab } = useProjectStore()
  const { projectModalOpen, setProjectModalOpen } = useDockUiStore()
  const [tab, setTab] = useState<Tab>('owned')
  const [searchQuery, setSearchQuery] = useState('')
  const [viewMode, setViewMode] = useState<ViewMode>(() => readPref(VIEW_KEY, ['list', 'card'] as const, 'list'))
  const [sortKey, setSortKey] = useState<SortKey>(() => readPref(SORT_KEY, ['name', 'created', 'updated'] as const, 'updated'))
  const [sortDir, setSortDir] = useState<SortDir>(() => readPref(SORTDIR_KEY, ['asc', 'desc'] as const, 'desc'))

  // Persist preferences as they change.
  useEffect(() => { writePref(VIEW_KEY, viewMode) }, [viewMode])
  useEffect(() => { writePref(SORT_KEY, sortKey) }, [sortKey])
  useEffect(() => { writePref(SORTDIR_KEY, sortDir) }, [sortDir])

  // Refresh on open so newly created / shared projects appear without a reload.
  useEffect(() => {
    if (projectModalOpen) fetchProjects()
  }, [projectModalOpen, fetchProjects])

  const owned = useMemo(() => projects.filter(isOwned), [projects])
  const shared = useMemo(() => projects.filter((p) => !isOwned(p)), [projects])

  // Default to whichever tab has content (prefer owned) each time it opens.
  useEffect(() => {
    if (projectModalOpen) {
      setSearchQuery('')
      setTab(owned.length === 0 && shared.length > 0 ? 'shared' : 'owned')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectModalOpen])

  const close = () => {
    setProjectModalOpen(false)
    setSearchQuery('')
  }

  // What a user may do with a project depends on their role for THAT project.
  // Builder + Workbench need design rights; Run is for any generated app.
  const canDesign = (p: ProjectData) =>
    !isRunOnlyAccess(p.access?.roles, p.access?.is_owner ?? false)
  const canRun = (p: ProjectData) =>
    !!p.application.generated || (p.navigation?.length ?? 0) > 0

  // The available modes for a project, in display order. Run-only users get
  // just "run"; design users get the builder modes too (and run if generated).
  type Mode = 'workbench' | 'builder' | 'run'
  const modesFor = (p: ProjectData): Mode[] => {
    const modes: Mode[] = []
    if (canDesign(p)) modes.push('workbench', 'builder')
    if (canRun(p)) modes.push('run')
    // A design user on a not-yet-generated draft still gets workbench/builder;
    // a generated app with no design rights still gets run. Never empty for a
    // real project, but guard anyway so the row is always actionable.
    if (modes.length === 0) modes.push(canDesign(p) ? 'workbench' : 'run')
    return modes
  }
  const defaultMode = (p: ProjectData): Mode =>
    canDesign(p) ? 'workbench' : (canRun(p) ? 'run' : 'workbench')

  const openInMode = (p: ProjectData, mode: Mode) => {
    const folderName = p.application.folder_name
    if (mode === 'run') {
      // Run always opens the live app in a NEW tab, so the user keeps their
      // current place (builder/workbench, or another app's Run view).
      const tenant = user?.tenant_id || '_'
      window.open(
        `/run/${encodeURIComponent(tenant)}/${encodeURIComponent(folderName)}`,
        '_blank',
        'noopener',
      )
      close()
      return
    }
    setActiveTab('next')
    selectProject(folderName)
    // Canonical deep-linkable URLs — keep back/forward + copy-paste working.
    const target = mode === 'builder'
      ? `/builder/${encodeURIComponent(folderName)}`
      : `/workbench/${encodeURIComponent(folderName)}/next`
    if (location.pathname !== target) navigate(target)
    close()
  }

  // Search matches the display title OR the project (folder) name — the folder
  // name is what other surfaces show (SSH agent console picker, Run URLs, agent
  // logs), so a user who only knows that name must still be able to find the app.
  const matchesSearch = (p: ProjectData) => {
    const q = searchQuery.trim().toLowerCase()
    if (!q) return true
    return (
      p.application.title.toLowerCase().includes(q) ||
      (p.application.folder_name || '').toLowerCase().includes(q)
    )
  }

  const sortList = (list: ProjectData[]) => {
    const arr = [...list]
    arr.sort((a, b) => {
      let r = 0
      if (sortKey === 'name') {
        r = a.application.title.localeCompare(b.application.title, undefined, { sensitivity: 'base' })
      } else if (sortKey === 'created') {
        r = tsOf(a.application.created_at) - tsOf(b.application.created_at)
      } else {
        r = tsOf(a.application.updated_at) - tsOf(b.application.updated_at)
      }
      if (r === 0) r = a.application.title.localeCompare(b.application.title, undefined, { sensitivity: 'base' })
      return sortDir === 'asc' ? r : -r
    })
    return arr
  }

  const ownedList = useMemo(
    () => sortList(owned.filter(matchesSearch)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [owned, searchQuery, sortKey, sortDir],
  )
  const sharedList = useMemo(
    () => sortList(shared.filter(matchesSearch)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [shared, searchQuery, sortKey, sortDir],
  )

  const MODE_META: Record<Mode, { label: string; Icon: typeof Play }> = {
    workbench: { label: 'Workbench', Icon: LayoutGrid },
    builder: { label: 'Builder', Icon: Sparkles },
    run: { label: 'Run', Icon: Play },
  }

  const modeButton = (p: ProjectData, mode: Mode, primary: boolean, compact = false) => {
    const { label, Icon } = MODE_META[mode]
    return (
      <button
        key={mode}
        onClick={(e) => { e.stopPropagation(); openInMode(p, mode) }}
        title={
          mode === 'run' ? `Run ${p.application.title} (opens in a new tab)`
            : mode === 'builder' ? `Open ${p.application.title} in Builder (chat-first)`
            : `Open ${p.application.title} in the Workbench`
        }
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 5,
          padding: compact ? '0.3rem 0.5rem' : '0.3rem 0.6rem',
          flex: compact ? '1 1 auto' : undefined,
          minWidth: 0,
          borderRadius: 'var(--radius-md)',
          fontSize: '0.8125rem',
          fontWeight: 600,
          whiteSpace: 'nowrap',
          cursor: 'pointer',
          border: `1px solid ${primary ? 'transparent' : 'var(--color-border)'}`,
          background: primary ? 'var(--color-accent)' : 'var(--color-bg)',
          color: primary ? '#fff' : 'var(--color-text)',
        }}
      >
        <Icon size={13} /> {label}
      </button>
    )
  }

  // The project (folder) name — the identifier every other surface uses (SSH
  // agent console picker, Run URLs, project storage, agent logs). Shown here so
  // a name seen elsewhere can be matched back to the app.
  const projectName = (p: ProjectData) => (
    <span
      title={`Project name: ${p.application.folder_name}`}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        maxWidth: '100%',
        minWidth: 0,
        fontFamily: 'var(--font-mono)',
        fontSize: '0.8125rem',
        color: 'var(--color-text-secondary)',
      }}
    >
      <FolderOpen size={11} style={{ flexShrink: 0, opacity: 0.7 }} />
      <span className="truncate">{p.application.folder_name}</span>
    </span>
  )

  // Second-line metadata: project name + owner (shared tab) + dates + roles.
  const metaLine = (p: ProjectData, isShared: boolean) => {
    const chips = isShared ? (p.access?.roles || []).map(prettyRole) : []
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 6,
          marginTop: 2,
          fontSize: '0.8125rem',
          color: 'var(--color-text-secondary)',
        }}
      >
        {projectName(p)}
        {isShared && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <OwnerAvatar name={p.access?.owner_name || ''} />
            Shared by {p.access?.owner_name || 'a teammate'}
          </span>
        )}
        <span>
          Created {fmtDate(p.application.created_at)} · Modified {fmtDate(p.application.updated_at)}
        </span>
        {chips.map((c) => (
          <span
            key={c}
            style={{
              padding: '0px 6px',
              borderRadius: 'var(--radius-full)',
              background: 'var(--color-bg)',
              border: '1px solid var(--color-border)',
              fontSize: '0.8125rem',
            }}
          >
            {c}
          </span>
        ))}
      </div>
    )
  }

  const row = (p: ProjectData, isShared: boolean) => {
    const isInvalid = p.application.title === 'Invalid project information'
    const active = currentProject?.application.folder_name === p.application.folder_name
    const modes = isInvalid ? [] : modesFor(p)
    const primaryMode = defaultMode(p)
    return (
      <div
        key={p.application.folder_name}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          width: '100%',
          padding: '0.625rem 0.75rem',
          borderRadius: 'var(--radius-md)',
          background: active ? 'var(--color-bg-secondary)' : 'transparent',
          border: `1px solid ${active ? 'var(--color-border)' : 'transparent'}`,
          transition: 'all var(--transition-fast)',
          marginBottom: 4,
        }}
        onMouseEnter={(e) => { if (!active) e.currentTarget.style.background = 'var(--color-bg-secondary)' }}
        onMouseLeave={(e) => { if (!active) e.currentTarget.style.background = 'transparent' }}
      >
        <button
          onClick={() => { if (!isInvalid) openInMode(p, primaryMode) }}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            flex: 1,
            minWidth: 0,
            background: 'none',
            border: 'none',
            padding: 0,
            textAlign: 'left',
            cursor: isInvalid ? 'default' : 'pointer',
          }}
        >
          <AppAvatar p={p} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span
                className="truncate"
                style={{ fontSize: '1rem', fontWeight: 600, color: isInvalid ? 'var(--color-error)' : 'var(--color-text)' }}
              >
                {isInvalid ? p.application.folder_name : p.application.title}
              </span>
              {!isInvalid && isDraft(p) && <DraftBadge />}
            </div>
            {!isInvalid && metaLine(p, isShared)}
          </div>
        </button>
        {/* Per-project mode picker — Workbench/Builder only when the user has
            design rights on this project; Run for any generated app. */}
        {modes.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
            {modes.map((m) => modeButton(p, m, m === primaryMode))}
          </div>
        )}
      </div>
    )
  }

  const card = (p: ProjectData, isShared: boolean) => {
    const isInvalid = p.application.title === 'Invalid project information'
    const active = currentProject?.application.folder_name === p.application.folder_name
    const modes = isInvalid ? [] : modesFor(p)
    const primaryMode = defaultMode(p)
    return (
      <div
        key={p.application.folder_name}
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
          padding: '0.875rem',
          borderRadius: 'var(--radius-lg)',
          background: active ? 'var(--color-bg-secondary)' : 'var(--color-bg)',
          border: `1px solid ${active ? 'var(--color-accent)' : 'var(--color-border)'}`,
          transition: 'all var(--transition-fast)',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--color-accent)' }}
        onMouseLeave={(e) => { e.currentTarget.style.borderColor = active ? 'var(--color-accent)' : 'var(--color-border)' }}
      >
        <button
          onClick={() => { if (!isInvalid) openInMode(p, primaryMode) }}
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            background: 'none',
            border: 'none',
            padding: 0,
            textAlign: 'left',
            cursor: isInvalid ? 'default' : 'pointer',
            minWidth: 0,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <AppAvatar p={p} size={40} />
            {!isInvalid && isDraft(p) && <DraftBadge />}
          </div>
          <span
            className="truncate"
            style={{ fontSize: '1rem', fontWeight: 600, color: isInvalid ? 'var(--color-error)' : 'var(--color-text)', maxWidth: '100%' }}
          >
            {isInvalid ? p.application.folder_name : p.application.title}
          </span>
          {!isInvalid && metaLine(p, isShared)}
        </button>
        {modes.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 6, width: '100%' }}>
            {modes.map((m) => modeButton(p, m, m === primaryMode, true))}
          </div>
        )}
      </div>
    )
  }

  const renderItems = (list: ProjectData[], isShared: boolean) => {
    if (viewMode === 'card') {
      return (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))',
            gap: 12,
            padding: '0.25rem',
          }}
        >
          {list.map((p) => card(p, isShared))}
        </div>
      )
    }
    return <>{list.map((p) => row(p, isShared))}</>
  }

  const emptyState = (
    icon: React.ReactNode, title: string, hint: string,
    action?: { label: string; onClick: () => void },
  ) => (
    <div style={{ padding: '2.5rem 1rem', textAlign: 'center' }}>
      <div style={{ opacity: 0.4, marginBottom: '0.75rem', display: 'flex', justifyContent: 'center' }}>{icon}</div>
      <p style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--color-text)', margin: '0 0 0.25rem' }}>{title}</p>
      <p style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)', margin: 0 }}>{hint}</p>
      {action && (
        <button className="btn btn-primary" style={{ marginTop: '1rem' }} onClick={action.onClick}>
          {action.label}
        </button>
      )}
    </div>
  )

  const tabButton = (key: Tab, label: string, count: number) => {
    const activeTabBtn = tab === key
    return (
      <button
        onClick={() => setTab(key)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          padding: '0.5rem 0.25rem',
          marginRight: '1.25rem',
          background: 'none',
          border: 'none',
          borderBottom: `2px solid ${activeTabBtn ? 'var(--color-accent)' : 'transparent'}`,
          color: activeTabBtn ? 'var(--color-text)' : 'var(--color-text-secondary)',
          fontSize: '0.9375rem',
          fontWeight: 600,
          cursor: 'pointer',
          marginBottom: -1,
        }}
      >
        {key === 'owned' ? <FolderOpen size={15} /> : <Users size={15} />}
        {label}
        <span
          style={{
            fontSize: '0.8125rem',
            fontWeight: 700,
            padding: '0px 6px',
            borderRadius: 'var(--radius-full)',
            background: activeTabBtn ? 'var(--color-accent)' : 'var(--color-bg-secondary)',
            color: activeTabBtn ? '#fff' : 'var(--color-text-secondary)',
          }}
        >
          {count}
        </span>
      </button>
    )
  }

  const viewToggleBtn = (mode: ViewMode, Icon: typeof List, label: string) => {
    const activeView = viewMode === mode
    return (
      <button
        onClick={() => setViewMode(mode)}
        title={`${label} view`}
        aria-pressed={activeView}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 30,
          height: 30,
          border: 'none',
          borderRadius: 'var(--radius-sm)',
          background: activeView ? 'var(--color-bg)' : 'transparent',
          boxShadow: activeView ? '0 1px 2px rgba(0,0,0,0.12)' : 'none',
          color: activeView ? 'var(--color-text)' : 'var(--color-text-secondary)',
          cursor: 'pointer',
        }}
      >
        <Icon size={16} />
      </button>
    )
  }

  return (
    <Modal
      open={projectModalOpen}
      onClose={close}
      title="Open an application"
      size="wide"
      resizable
      maximizable
      storageKey={SIZE_KEY}
    >
      <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
        {/* One-line hint so the per-app mode buttons are self-explanatory. */}
        <p
          style={{
            margin: '0 0 0.75rem',
            fontSize: '0.875rem',
            color: 'var(--color-text-secondary)',
            flexShrink: 0,
          }}
        >
          Choose how to open each app — <strong>Run</strong> to use it, or{' '}
          <strong>Workbench</strong> / <strong>Builder</strong> where you have edit access.
        </p>

        {/* Tabs */}
        <div style={{ display: 'flex', borderBottom: '1px solid var(--color-border)', marginBottom: '0.75rem', flexShrink: 0 }}>
          {tabButton('owned', 'Your projects', owned.length)}
          {tabButton('shared', 'Shared with you', shared.length)}
        </div>

        {/* Toolbar: search + sort + view toggle */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            marginBottom: '0.75rem',
            flexWrap: 'wrap',
            flexShrink: 0,
          }}
        >
          <div style={{ position: 'relative', flex: 1, minWidth: 160 }}>
            <Search
              size={15}
              style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', color: 'var(--color-text-secondary)', opacity: 0.6 }}
            />
            <input
              type="search"
              autoFocus
              placeholder={tab === 'owned'
                ? 'Search your projects by title or name…'
                : 'Search shared projects by title or name…'}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              autoComplete="off"
              data-form-type="other"
              data-lpignore="true"
              style={{
                width: '100%',
                padding: '0.5rem 0.75rem 0.5rem 2rem',
                background: 'var(--color-bg)',
                border: '1px solid var(--color-border)',
                borderRadius: 'var(--radius-md)',
                color: 'var(--color-text)',
                fontSize: '0.9375rem',
              }}
            />
          </div>

          {/* Sort control */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <select
              value={sortKey}
              onChange={(e) => setSortKey(e.target.value as SortKey)}
              title="Sort by"
              style={{
                padding: '0.45rem 0.5rem',
                background: 'var(--color-bg)',
                border: '1px solid var(--color-border)',
                borderRadius: 'var(--radius-md)',
                color: 'var(--color-text)',
                fontSize: '0.875rem',
                cursor: 'pointer',
              }}
            >
              {SORT_OPTIONS.map((o) => (
                <option key={o.key} value={o.key}>{o.label}</option>
              ))}
            </select>
            <button
              onClick={() => setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))}
              title={sortDir === 'asc' ? 'Ascending' : 'Descending'}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 32,
                height: 32,
                background: 'var(--color-bg)',
                border: '1px solid var(--color-border)',
                borderRadius: 'var(--radius-md)',
                color: 'var(--color-text-secondary)',
                cursor: 'pointer',
              }}
            >
              {sortDir === 'asc' ? <ArrowUp size={15} /> : <ArrowDown size={15} />}
            </button>
          </div>

          {/* View toggle */}
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 2,
              padding: 2,
              background: 'var(--color-bg-secondary)',
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius-md)',
            }}
          >
            {viewToggleBtn('list', List, 'List')}
            {viewToggleBtn('card', LayoutGrid, 'Card')}
          </div>
        </div>

        {/* Scrollable list region — fills the resizable modal. */}
        <div style={{ flex: 1, minHeight: 120, overflowY: 'auto', overflowX: 'hidden' }}>
          {tab === 'owned' && (
            ownedList.length > 0
              ? renderItems(ownedList, false)
              : owned.length === 0
                ? emptyState(<Layers size={34} />, 'No projects yet', 'Describe the app you want and Twasta builds the first version.',
                    { label: 'Create new application', onClick: () => { setProjectModalOpen(false); navigate('/builder') } })
                : emptyState(<Search size={34} />, 'No matches', 'Try a different search term.')
          )}

          {tab === 'shared' && (
            sharedList.length > 0
              ? renderItems(sharedList, true)
              : shared.length === 0
                ? emptyState(
                    <Users size={34} />,
                    'Nothing shared with you yet',
                    'Projects that teammates add you to will appear here.',
                  )
                : emptyState(<Search size={34} />, 'No matches', 'Try a different search term.')
          )}
        </div>
      </div>
    </Modal>
  )
}
